import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { buscarEnContable, getActiveProvider } from '../../api/common';
import { getStates, getCities } from '../../api/states';

const EMPTY_FORM = {
  identificacion: '',
  nombre: '',
  apellido: '',
  direccion: '',
  ciudad: '',
  departamento: '',
  telefono: '',
  correo: '',
  contacto: '',
  cargoContacto: '',
  contactoFactura: '',
  correoFacturación: '',
  fechaCierreContable: '',
};

const REQUIRED_FIELDS = {
  correo:       'Correo',
  contacto:     'Contacto',
  cargoContacto:'Cargo Contacto',
};

const ClienteModal = ({ show, onHide, usuario, onSave }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [proveedorActivo, setProveedorActivo] = useState(null);

  // Cargar departamentos al montar
  useEffect(() => {
    getStates()
      .then((r) => r.json())
      .then((data) => setStates(data))
      .catch(() => {});
  }, []);

  // Cargar proveedor activo al abrir
  useEffect(() => {
    if (!show) return;
    getActiveProvider()
      .then(res => {
        const lista = res?.data?.data;
        const activo = Array.isArray(lista) ? lista.find(p => p.active) : null;
        setProveedorActivo(activo || false);
      })
      .catch(() => setProveedorActivo(false));
  }, [show]);

  // Cargar ciudades cuando cambia el departamento
  useEffect(() => {
    if (!formData.departamento) {
      setCities([]);
      return;
    }
    getCities(formData.departamento)
      .then((r) => r.json())
      .then((data) => setCities(data))
      .catch(() => setCities([]));
  }, [formData.departamento]);

  useEffect(() => {
    if (usuario) {
      setFormData({
        identificacion: usuario.identificacion || '',
        nombre: usuario.nombre || '',
        apellido: usuario.apellido || '',
        direccion: usuario.direccion || '',
        ciudad: usuario.ciudad || '',
        departamento: usuario.departamento || '',
        telefono: usuario.telefono || '',
        correo: usuario.correo || '',
        contacto: usuario.contacto || '',
        cargoContacto: usuario.cargoContacto || '',
        contactoFactura: usuario.contactoFactura || '',
        correoFacturación: usuario.correoFacturación || '',
        fechaCierreContable: usuario.fechaCierreContable || '',
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setSearchResult(null);
    setErrors({});
  }, [usuario, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBuscar = async () => {
    if (!formData.identificacion.trim()) {
      Swal.fire('Atención', 'Ingresa la identificación antes de buscar', 'warning');
      return;
    }

    setSearchLoading(true);
    setSearchResult(null);
    try {
      const res = await buscarEnContable(formData.identificacion);
      const data = res.data || res;
      if (data.found) {
        setSearchResult({ found: true, providerName: data.providerName, providerId: data.providerId, mapeado: data.mapeado, contacto_externo: data.contacto_externo });
        // Pre-fill form from mapped data
        const m = data.mapeado || {};
        setFormData((prev) => ({
          ...prev,
          nombre: m.nombre || prev.nombre,
          direccion: m.direccion || prev.direccion,
          ciudad: m.ciudad || prev.ciudad,
          departamento: m.departamento || prev.departamento,
          telefono: m.telefono || prev.telefono,
          correo: m.correo || prev.correo,
        }));
      } else {
        setSearchResult({ found: false });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.join(', ') || err.message;
      Swal.fire('Error', msg, 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleGuardar = () => {
    // Validar campos obligatorios
    const newErrors = {};
    Object.entries(REQUIRED_FIELDS).forEach(([field, label]) => {
      if (!formData[field]?.trim()) newErrors[field] = `${label} es obligatorio`;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const payload = { ...formData };

    // If found in accounting, include contabilidad data
    if (searchResult?.found && searchResult.contacto_externo) {
      payload.contabilidad = {
        id: searchResult.contacto_externo.id || searchResult.contacto_externo.Id || null,
        providerName: searchResult.providerName,
        providerId: searchResult.providerId || null,
        lastSync: new Date().toISOString(),
        ...(searchResult.mapeado?.contabilidad || {}),
      };
    }

    onSave(payload);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{usuario ? 'Editar Cliente' : 'Nuevo Cliente'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* ESTADO DE SINCRONIZACIÓN CONTABLE (solo al editar) */}
        {usuario && (() => {
          const c = usuario.contabilidad;
          const sincronizado = c?.id && proveedorActivo && c.providerId === String(proveedorActivo._id);
          const otroproveedor = c?.id && proveedorActivo && c.providerId && c.providerId !== String(proveedorActivo._id);

          if (sincronizado) {
            return (
              <Alert variant="success" className="py-2 mb-3 d-flex align-items-center gap-2">
                <i className="bi bi-check-circle-fill"></i>
                <span>
                  Sincronizado con <strong>{c.providerName || proveedorActivo.display_name}</strong>
                  {c.identification && <> · NIT contable: <strong>{c.identification}</strong></>}
                  {c.lastSync && <> · última sync: {new Date(c.lastSync).toLocaleDateString('es-CO')}</>}
                </span>
              </Alert>
            );
          }
          if (otroproveedor) {
            return (
              <Alert variant="warning" className="py-2 mb-3">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Sincronizado con un proveedor diferente al activo. Use "Buscar en contable" para re-sincronizar.
              </Alert>
            );
          }
          if (proveedorActivo === false) {
            return (
              <Alert variant="secondary" className="py-2 mb-3">
                <i className="bi bi-building me-2"></i>
                Sin proveedor contable activo configurado.
              </Alert>
            );
          }
          // no sincronizado y hay proveedor activo
          return (
            <Alert variant="warning" className="py-2 mb-3 d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-exclamation-circle me-2"></i>
                Cliente no sincronizado con el software contable.
              </span>
              <Button
                size="sm"
                variant="warning"
                onClick={handleBuscar}
                disabled={searchLoading}
              >
                {searchLoading
                  ? <Spinner animation="border" size="sm" />
                  : <><i className="bi bi-arrow-repeat me-1"></i>Sincronizar</>}
              </Button>
            </Alert>
          );
        })()}
        <Row className="mb-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Identificación</Form.Label>
              <Form.Control
                name="identificacion"
                value={formData.identificacion}
                onChange={handleChange}
                placeholder="NIT / CC"
              />
            </Form.Group>
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button
              variant="outline-primary"
              onClick={handleBuscar}
              disabled={searchLoading}
              className="w-100"
            >
              {searchLoading ? <Spinner animation="border" size="sm" /> : '🔍 Buscar en contable'}
            </Button>
          </Col>
        </Row>

        {searchResult?.found && (
          <Alert variant="success" className="mb-3">
            ✅ Encontrado en {searchResult.providerName}: {searchResult.mapeado?.nombre || ''}
          </Alert>
        )}
        {searchResult && !searchResult.found && (
          <Alert variant="warning" className="mb-3">
            ⚠️ No encontrado en el software contable
          </Alert>
        )}

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Razon social  </Form.Label>
              <Form.Control name="nombre" value={formData.nombre} onChange={handleChange} />
            </Form.Group>
          </Col>
          {/* <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Apellido / Contacto Empresa</Form.Label>
              <Form.Control name="apellido" value={formData.apellido} onChange={handleChange} />
            </Form.Group>
          </Col>*/}
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Dirección</Form.Label>
              <Form.Control name="direccion" value={formData.direccion} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Departamento</Form.Label>
              <Form.Select
                name="departamento"
                value={formData.departamento}
                onChange={handleChange}
              >
                <option value="">-- Selecciona --</option>
                {states.map((s, idx) => (
                  <option key={idx} value={s.name}>{s.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group className="mb-3">
              <Form.Label>Ciudad</Form.Label>
              <Form.Select
                name="ciudad"
                value={formData.ciudad}
                onChange={handleChange}
                disabled={!formData.departamento || cities.length === 0}
              >
                <option value="">-- Selecciona --</option>
                {cities.map((c, idx) => (
                  <option key={idx} value={c.name}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control name="telefono" value={formData.telefono} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Correo <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="correo"
                value={formData.correo}
                onChange={handleChange}
                isInvalid={!!errors.correo}
              />
              <Form.Control.Feedback type="invalid">{errors.correo}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Contacto <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="contacto"
                value={formData.contacto}
                onChange={handleChange}
                isInvalid={!!errors.contacto}
              />
              <Form.Control.Feedback type="invalid">{errors.contacto}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Cargo Contacto <span className="text-danger">*</span></Form.Label>
              <Form.Control
                name="cargoContacto"
                value={formData.cargoContacto}
                onChange={handleChange}
                isInvalid={!!errors.cargoContacto}
              />
              <Form.Control.Feedback type="invalid">{errors.cargoContacto}</Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Contacto Factura</Form.Label>
              <Form.Control name="contactoFactura" value={formData.contactoFactura} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Correo Facturación</Form.Label>
              <Form.Control name="correoFacturación" value={formData.correoFacturación} onChange={handleChange} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Fecha Cierre Contable</Form.Label>
              <Form.Control name="fechaCierreContable" value={formData.fechaCierreContable} onChange={handleChange} />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar}>
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClienteModal;
